import React, { useContext, useState } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';

import { login } from 'src/services/auth-service/login.service';
import { signup } from 'src/services/auth-service/signup.service';
import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import logger from 'logger';
import { getNextConfigValues } from 'next-config';
import { useRouter } from 'next/router';
import useAnalytics from 'src/analytics/useAnalytics';
import UserContext, { UserContextInterface } from 'src/context/user';
import { GENERIC_ERROR_MESSAGE, RegexPatterns } from 'src/utils/constants';
import { object, string } from 'yup';

import PasswordValidation from './PasswordValidation';

const {
  publicRuntimeConfig: { frontendUrls },
} = getNextConfigValues();

const Form: React.FC<Props> = ({
  isSignup,
  setIsSignup,
  defaultValues,
  referralCode = undefined,
  recaptchaToken,
  setHasSignupError,
}) => {
  return isSignup ? (
    <SignupForm
      referralCode={referralCode}
      defaultValues={defaultValues as SignupFormValues}
      recaptchaToken={recaptchaToken}
      setHasSignupError={setHasSignupError}
    />
  ) : (
    <LoginForm
      defaultValues={defaultValues as SignupFormValues}
      setIsSignup={setIsSignup}
      isSignup={!!isSignup}
    />
  );
};

const LoginForm = ({
  defaultValues,
  setIsSignup,
  isSignup,
}: {
  defaultValues: Pick<SignupFormValues, 'email' | 'password'>;
  setIsSignup: (arg: boolean) => unknown;
  isSignup: boolean;
}) => {
  const router = useRouter();
  const isAuthPage =
    router.pathname === '/signup' || router.pathname === '/login';
  const { setUser } = useContext(UserContext) as UserContextInterface;
  const [isPasswordShown, setPasswordShown] = useState<boolean>(false);
  const [isPasswordFocused, setPasswordFocused] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const theme = useTheme();
  const {
    control,
    formState: { isValid },
    setError,
    getValues,
    handleSubmit,
  } = useForm({
    resolver: yupResolver(loginSchema),
    mode: 'onChange',
    defaultValues,
  });
  const togglePasswordShown = () => setPasswordShown(!isPasswordShown);
  const togglePasswordFocused = () => setPasswordFocused(!isPasswordFocused);
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm'),
  );
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [hasLoginError, setHasLoginError] = useState(false);

  const onSubmit = async () => {
    setIsLoading(true);
    setHasLoginError(false);
    const values = getValues();
    try {
      const result = await login({
        email: values.email!,
        password,
        rememberMe: true,
      });

      if (result.ok) {
        setUser(result.user);
        if (isAuthPage) {
          router.push(frontendUrls.customerDashboard);
        }
      } else if (result.reason === 'invalid_credentials') {
        setHasLoginError(true);
        setError('password', {
          type: 'custom',
          message: 'Invalid email or password',
        });
      } else {
        setHasLoginError(true);
        setShowErrorAlert(true);
      }
    } catch (err: any) {
      setHasLoginError(true);
      setShowErrorAlert(true);
      logger.info({
        msg: 'LoginUnexpectedError',
        noticable: err,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {showErrorAlert && (
        <Alert severity="error" sx={{ marginBottom: '1rem' }}>
          {GENERIC_ERROR_MESSAGE}
        </Alert>
      )}
      <FormControl fullWidth>
        <Grid
          container
          spacing={2}
          columns={12}
          sx={{
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Grid>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                formState: { errors },
              }) => (
                <TextField
                  color="primary"
                  sx={isSmallScreen ? mobileInputStyles : largeInputStyles}
                  label="Email"
                  required
                  value={value}
                  onChange={onChange}
                  error={!!errors?.email?.message || hasLoginError}
                  helperText={
                    errors?.email?.message?.includes('Invalid email') &&
                    errors?.email?.message
                  }
                />
              )}
            />
          </Grid>
          <Grid>
            <Controller
              name="password"
              control={control}
              defaultValue=""
              render={({ field: { onChange }, formState: { errors } }) => (
                <TextField
                  color="primary"
                  sx={isSmallScreen ? mobileInputStyles : largeInputStyles}
                  label="Password"
                  required
                  type={isPasswordShown ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    onChange(e.target.value);
                  }}
                  onFocus={togglePasswordFocused}
                  onBlur={togglePasswordFocused}
                  error={!!errors?.password?.message || hasLoginError}
                  helperText={errors?.password?.message ?? ''}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={togglePasswordShown} edge="end">
                            {isPasswordShown ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid>
            <Grid
              container
              direction="row"
              sx={[
                {
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                },
                isSmallScreen ? mobileInputStyles : largeInputStyles,
              ]}
            >
              <Grid>
                <Link
                  href="/forgot-password"
                  variant="caption"
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                  }}
                >
                  Forgot your password?
                </Link>
              </Grid>
            </Grid>
          </Grid>
          <Grid sx={{ marginTop: isSmallScreen ? '0.5rem' : '1rem' }}>
            <Grid
              container
              direction="row"
              sx={[
                {
                  justifyContent: 'space-between',
                  alignItems: 'center',
                },
                isSmallScreen ? mobileInputStyles : largeInputStyles,
              ]}
            >
              <Grid>
                <Link
                  onClick={() =>
                    isAuthPage
                      ? router.push(isSignup ? '/login' : '/signup')
                      : setIsSignup(!isSignup)
                  }
                  sx={{ textDecoration: 'none' }}
                >
                  <Button
                    size="medium"
                    variant="text"
                    sx={{
                      color: theme.palette.primary.main,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Sign up instead
                  </Button>
                </Link>
              </Grid>
              <Grid sx={{ width: 'initial' }}>
                <Button
                  type="submit"
                  size="large"
                  variant={isLoading ? 'outlined' : 'contained'}
                  disabled={!isValid || isLoading}
                  sx={{
                    whiteSpace: 'nowrap',
                    width: isSignup ? '100%' : 'initial',
                  }}
                  onClick={handleSubmit(onSubmit)}
                >
                  {isLoading && (
                    <CircularProgress
                      sx={{
                        marginRight: '0.25rem',
                        color: theme.palette.grey[300],
                      }}
                      size={25}
                    />
                  )}
                  {/* CTA button moved to next line when loading on mobile, logic below handles all cases */}
                  {!isSmallScreen && 'LOG IN'}
                  {!isLoading && isSmallScreen && 'LOG IN'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
    </>
  );
};

const SignupForm = ({
  referralCode,
  defaultValues,
  recaptchaToken,
  setHasSignupError,
}: {
  referralCode: string | undefined;
  defaultValues: SignupFormValues;
  recaptchaToken: string;
  setHasSignupError: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { track } = useAnalytics();
  const router = useRouter();
  const theme = useTheme();
  const isAuthPage = router.pathname === '/signup';
  const { setUser, tenantId } = useContext(UserContext) as UserContextInterface;
  const [isPasswordShown, setPasswordShown] = useState<boolean>(false);
  const [isPasswordFocused, setPasswordFocused] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const togglePasswordShown = () => setPasswordShown(!isPasswordShown);
  const togglePasswordFocused = () => setPasswordFocused(!isPasswordFocused);
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm'),
  );
  const {
    control,
    formState: { isValid, errors },
    setError,
    getValues,
    handleSubmit,
  } = useForm<SignupFormValues>({
    resolver: yupResolver(signupSchema) as Resolver<SignupFormValues>,
    mode: 'onChange',
    defaultValues,
  });
  const [showErrorAlert, setShowErrorAlert] = useState(false);

  const onSubmit = async () => {
    setIsLoading(true);
    const values = getValues();
    try {
      // button is disabled when these form fields are blank, so this function
      // is only called when email/firstName/lastName are set and valid — the
      // non-null assertions below are safe.
      const result = await signup({
        firstName: values.firstName!,
        lastName: values.lastName!,
        email: values.email!,
        password,
        recaptchaToken,
        referralCode,
      });

      if (result.ok) {
        setShowErrorAlert(false);
        track('Account Created', {
          location: 'signup',
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          userId: result.user.id,
          tenantId,
        });
        setUser(result.user);
        if (isAuthPage) {
          router.push('/get-started');
        }
        // @todo: add onSuccessCallback here
      } else if (result.reason === 'validation') {
        const { fieldErrors } = result;
        if (fieldErrors.email === 'has already been taken') {
          track('Signup failed -- duplicate email', { email: values.email });
        }
        for (const [field, message] of Object.entries(fieldErrors)) {
          if (message) {
            setError(field as keyof typeof fieldErrors, {
              type: 'custom',
              message,
            });
          }
        }
      } else if (result.reason === 'blocked') {
        // Recaptcha rejected the request — surface the parent "system flagged
        // your request" alert rather than a field-level error.
        setHasSignupError(true);
      } else {
        setShowErrorAlert(true);
      }
    } catch (err: any) {
      setHasSignupError(true);
      logger.info({
        msg: 'SignupUnexpectedError',
        noticable: err,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {showErrorAlert && (
        <Alert severity="error" sx={{ marginBottom: '1rem' }}>
          {GENERIC_ERROR_MESSAGE}
        </Alert>
      )}
      <FormControl fullWidth>
        <Grid
          container
          spacing={2}
          columns={12}
          sx={{
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Grid>
            <Grid
              container
              spacing={2}
              sx={[
                {
                  flexDirection: isSmallScreen ? 'column' : 'row',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                },
                !isSmallScreen && { width: '25rem' },
              ]}
            >
              <Grid size={6}>
                <Controller
                  name="firstName"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    formState: { errors },
                  }) => (
                    <TextField
                      sx={isSmallScreen ? mobileInputStyles : undefined}
                      color="primary"
                      label="First name"
                      value={value}
                      required
                      onChange={onChange}
                      error={!!errors?.firstName}
                      helperText={
                        errors?.firstName ? errors?.firstName?.message : null
                      }
                    />
                  )}
                />
              </Grid>
              <Grid size={6}>
                <Controller
                  name="lastName"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    formState: { errors },
                  }) => (
                    <TextField
                      sx={isSmallScreen ? mobileInputStyles : undefined}
                      color="primary"
                      label="Last name"
                      required
                      value={value}
                      onChange={onChange}
                      error={!!errors?.lastName}
                      helperText={errors ? errors?.lastName?.message : null}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                formState: { errors },
              }) => (
                <TextField
                  sx={isSmallScreen ? mobileInputStyles : largeInputStyles}
                  color="primary"
                  label="Email"
                  required
                  value={value}
                  onChange={onChange}
                  error={!!errors?.email}
                  helperText={errors?.email ? errors?.email?.message : null}
                />
              )}
            />
          </Grid>
          <Grid>
            <Controller
              name="password"
              control={control}
              defaultValue=""
              render={({ field: { onChange }, formState: { errors } }) => (
                <TextField
                  color="primary"
                  sx={isSmallScreen ? mobileInputStyles : largeInputStyles}
                  label="Password"
                  required
                  type={isPasswordShown ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    onChange(e.target.value);
                  }}
                  onFocus={togglePasswordFocused}
                  onBlur={togglePasswordFocused}
                  error={!!errors?.password?.message}
                  helperText={errors?.password?.message ?? ''}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={togglePasswordShown} edge="end">
                            {isPasswordShown ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid>
            <Grid
              container
              direction="row"
              sx={[
                {
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                },
                isSmallScreen ? mobileInputStyles : largeInputStyles,
              ]}
            >
              <Grid>
                {isPasswordFocused && errors?.password && (
                  <PasswordValidation password={password} />
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid sx={{ marginTop: isSmallScreen ? '0.5rem' : '1rem' }}>
            <Grid
              container
              direction="row"
              sx={[
                {
                  justifyContent: 'space-between',
                  alignItems: 'center',
                },
                isSmallScreen ? mobileInputStyles : largeInputStyles,
              ]}
            >
              <Grid sx={{ width: '100%' }}>
                <Button
                  type="submit"
                  size="large"
                  variant={isLoading ? 'outlined' : 'contained'}
                  disabled={!isValid || isLoading}
                  sx={{
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                  onClick={handleSubmit(onSubmit)}
                >
                  {isLoading && (
                    <CircularProgress
                      sx={{
                        marginRight: '0.25rem',
                        color: theme.palette.grey[300],
                      }}
                      size={25}
                    />
                  )}
                  {/* CTA button moved to next line when loading on mobile, logic below handles all cases */}
                  {!isSmallScreen && 'SIGN UP'}
                  {!isLoading && isSmallScreen && 'SIGN UP'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </FormControl>
    </>
  );
};

export default Form;

const largeInputStyles = { width: '25rem' };
const mobileInputStyles = { width: '15.5rem' };

type SignupFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  referralCode?: string;
};

interface Props {
  isSignup: boolean | null;
  setIsSignup: (isSignup: boolean) => void;
  onSuccess?: () => any;
  defaultValues?: Partial<SignupFormValues>;
  referralCode?: string;
  recaptchaToken: string;
  setHasSignupError: React.Dispatch<React.SetStateAction<boolean>>;
}

const signupSchema = object({
  firstName: string().required('First name required').defined().trim(),
  lastName: string().required('Last name required').defined().trim(),
  email: string()
    .email('Please enter a valid email')
    .required('Email required')
    .defined()
    .trim(),
  password: string()
    .required('Password required')
    .matches(
      RegexPatterns.IS_BIGGER_THAN_SIX_CHARS,
      'Passwords must be at least 6 characters long',
    )
    .matches(
      RegexPatterns.HAS_UPPERCASE_LETTER,
      'Must contain at least one uppercase letter',
    )
    .matches(RegexPatterns.HAS_NUMBER, 'Must contain at least one number')
    .matches(
      RegexPatterns.HAS_SPECIAL_CHAR,
      'Must contain at least one symbol (@, !, #...)',
    )
    .defined(),
  referralCode: string().nullable().optional(),
});

const loginSchema = object({
  email: string().required(' ').email(' ').defined().trim(),
  password: string().required().defined(),
});
